/**
 * ONE's per-env Firebase Web App config resolution.
 *
 * ADR-132 Amendment 4: ONE registers against its OWN env-major Firebase Web
 * App entries in the `aegix-aim` platform project (`dev-aegix-one-react` /
 * `stage-aegix-one-react` / `aegix-one-react`), never against AIM's shared
 * entry. The entry display name doubles as the `app_identifier` the backend
 * routes on (`fcm_routing.ONE_APP_IDENTIFIERS` — exact set membership).
 *
 * Env selection follows the ADR-006 runtime-env pattern for page code: the
 * deployed `env-config.js` (generated per env by `generate-env-config.js`)
 * stamps `window.__ENV.NX_SENTRY_ENVIROMENT`, and this module maps it onto
 * the env-major triplet in `firebaseEnvConfigs.json`. The service worker
 * cannot read `window.__ENV`, so its copy of the same triplet entry is baked
 * into `public/firebase-sw-config.js` at generate time.
 *
 * A missing entry (`firebase: null` — console entry pending creation) must
 * fail LOUD in the console and skip registration; a silent skip is exactly
 * the failure mode Amendment 4 exists to prevent.
 */
import firebaseEnvConfigs from './firebaseEnvConfigs.json';

export interface OneFirebaseWebConfig {
  apiKey: string;
  authDomain: string;
  projectId: string;
  storageBucket: string;
  messagingSenderId: string;
  appId: string;
  measurementId?: string;
}

export interface OnePushEnvEntry {
  /** Firebase Web App entry display name == backend `app_identifier`. */
  appIdentifier: string;
  /** aegix-aim project Web Push certificate public key (project-scoped). */
  vapidKey: string;
  /** `null` while the env's console entry is pending creation. */
  firebase: OneFirebaseWebConfig | null;
}

export type OnePushEnv = 'development' | 'staging' | 'production';

const ENV_CONFIGS = firebaseEnvConfigs as unknown as Record<
  OnePushEnv,
  OnePushEnvEntry
>;

/**
 * Map the deployed `NX_SENTRY_ENVIROMENT` value onto the env-major triplet.
 * CI stamps `development` / `staging` / `production`; the committed local
 * default is `develop` — anything that isn't explicitly staging/production
 * resolves to the development entry.
 */
export function resolveOnePushEnv(sentryEnv: string | undefined): OnePushEnv {
  if (sentryEnv === 'production') return 'production';
  if (sentryEnv === 'staging') return 'staging';
  return 'development';
}

export interface ResolvedOnePushConfig {
  env: OnePushEnv;
  appIdentifier: string;
  vapidKey: string;
  firebase: OneFirebaseWebConfig;
}

/**
 * The current environment's push config, or `null` when the env's Firebase
 * Web App entry has not been created yet — in which case this logs a LOUD
 * console error every time registration is attempted (ADR-132 A4: an entry
 * whose push credential/config is missing must fail loud, never surface as
 * a silent skip or a generic 401).
 */
export function getOnePushConfig(): ResolvedOnePushConfig | null {
  const env = resolveOnePushEnv(window.__ENV?.NX_SENTRY_ENVIROMENT);
  const entry = ENV_CONFIGS[env];
  if (!entry || !entry.firebase) {
    console.error(
      `[aegix-one push] No Firebase Web App config for env "${env}" — ` +
        `push registration SKIPPED. The "${entry?.appIdentifier ?? env}" ` +
        `Firebase Web App entry is pending console creation (ADR-132 ` +
        `Amendment 4). Fill apps/aegix-one-react/src/push/firebaseEnvConfigs.json ` +
        `once the entry exists.`,
    );
    return null;
  }
  return {
    env,
    appIdentifier: entry.appIdentifier,
    vapidKey: entry.vapidKey,
    firebase: entry.firebase,
  };
}
